import api, { setCaregiverAuthToken } from './api';

/**
 * Resolve the SC's active sponsor scope for caregiver setup URLs/QRs.
 * Prefers an explicitly-selected sponsor (multi-sponsor internal users), then
 * falls back to the sponsor on the cached user record. Returns '' if unknown.
 */
export const getActiveSponsorId = () => {
  try {
    const selected = JSON.parse(localStorage.getItem('selectedSponsor') || 'null');
    if (selected?.id) return String(selected.id);
  } catch (_) {
    /* ignore malformed selectedSponsor */
  }
  try {
    const user = JSON.parse(localStorage.getItem('user') || 'null');
    if (user?.sponsor_id) return String(user.sponsor_id);
  } catch (_) {
    /* ignore malformed user */
  }
  return '';
};

/**
 * Where a signed-out caregiver signs in again: the caregiver sign-in with the
 * Study Code they signed in with already filled in (FECOA-2156), so a
 * caregiver who came in through the participant study link need not know it.
 * Call it before logout(), which clears the session it reads.
 */
export const caregiverSignInPath = ({ expired = false } = {}) => {
  let studyCode = '';
  try {
    studyCode = JSON.parse(sessionStorage.getItem('caregiverSession') || 'null')?.study_code || '';
  } catch (_) {
    /* ignore a malformed session */
  }
  const params = new URLSearchParams();
  if (studyCode) params.set('study', studyCode);
  if (expired) params.set('expired', '1');
  const query = params.toString();
  return `/caregiver-login${query ? `?${query}` : ''}`;
};

/**
 * Caregiver service (FECOA-1320).
 *
 * All endpoints are sponsor-scoped on the backend; the SC's JWT carries the
 * sponsor and site claims that gate access. The service is intentionally thin
 * — view-level role/PII logic lives in the backend serializer split.
 */
const caregiverService = {
  /**
   * Sponsor-wide exact-match search by email or caregiver_id.
   * Returns { matches: Caregiver[] } with no site/study linkage data.
   */
  searchExisting: async (q) => {
    const response = await api.get('/api/caregivers/search/', { params: { q } });
    return response.data; // { matches: [...] }
  },

  /**
   * Create a new caregiver. Optional initial link if participant_id/site_id
   * /relationship_type are included.
   *
   * payload shape:
   *   {
   *     first_name, last_name, email, phone?,
   *     participant_id?, site_id?, relationship_type?,
   *     invitation_method: 'in_clinic' | 'email',
   *     temp_pin?,                          // required when in_clinic
   *     informed_consent_verified: true,    // required
   *   }
   */
  create: async (payload) => {
    const response = await api.post('/api/caregivers/', payload);
    return response.data;
  },

  /**
   * Link an existing caregiver to a participant. Used by the FECOA-1369 flow
   * after a successful sponsor-wide search hit.
   */
  linkToParticipant: async (caregiverId, payload) => {
    const response = await api.post(`/api/caregivers/${caregiverId}/links/`, payload);
    return response.data;
  },

  /**
   * Deactivate a caregiver-participant link. Reactivation is intentionally
   * unsupported by the backend (FECOA-1374).
   */
  deactivateLink: async (caregiverId, linkId) => {
    const response = await api.post(`/api/caregivers/${caregiverId}/links/${linkId}/deactivate/`);
    return response.data;
  },

  /**
   * SC-initiated PIN reset (FECOA-1373). Returns { temporary_pin } that the
   * SC must communicate out-of-band; the value is shown once and never emailed.
   */
  resetPin: async (caregiverId) => {
    const response = await api.post(`/api/caregivers/${caregiverId}/reset-pin/`);
    return response.data;
  },

  /**
   * Re-send the email invitation (FECOA-1362). Allowed only while the
   * caregiver is Pending First Login and has an email on file. Invalidates
   * any prior outstanding invite tokens.
   */
  resendInvite: async (caregiverId) => {
    const response = await api.post(`/api/caregivers/${caregiverId}/resend-invite/`);
    return response.data;
  },

  /**
   * SC-initiated manual lock. Distinct from auto-lock-on-failed-attempts.
   * Refused for Pending First Login state (409). Reset PIN clears the lock.
   */
  lock: async (caregiverId) => {
    const response = await api.post(`/api/caregivers/${caregiverId}/lock/`);
    return response.data;
  },

  /**
   * Admin unlock — clears the lock without rotating the PIN.
   */
  unlock: async (caregiverId) => {
    const response = await api.post(`/api/caregivers/${caregiverId}/unlock/`);
    return response.data;
  },

  /**
   * Hard delete — superuser only. Wipes the caregiver and all dependent
   * link/invite rows. Production withdrawal is link deactivation; this is
   * for test cleanup.
   */
  hardDelete: async (caregiverId) => {
    const response = await api.delete(`/api/caregivers/${caregiverId}/`);
    return response.data;
  },

  /**
   * Caregivers tab data for a participant (FECOA-1321).
   * Returns { allow_caregivers, caregivers: [{ link_id, status, caregiver, ... }] }.
   */
  listForParticipant: async (participantId) => {
    const response = await api.get(`/api/participants/${participantId}/caregivers/`);
    return response.data;
  },

  /**
   * Admin-level study-scoped caregiver list. Returns every caregiver who has
   * a link (active or historical) to a participant in the study, grouped with
   * link history. Used by the study Caregivers tab.
   */
  listForStudy: async (studyId) => {
    const response = await api.get(`/api/studies/${studyId}/caregivers/`);
    return response.data;
  },

  /**
   * Single caregiver detail (FECOA-1371).
   * Backend masks PII based on the caller's role.
   */
  get: async (caregiverId) => {
    const response = await api.get(`/api/caregivers/${caregiverId}/`);
    return response.data;
  },

  /**
   * Update caregiver profile (FECOA-1372). Affects all linked participants.
   */
  update: async (caregiverId, payload) => {
    const response = await api.patch(`/api/caregivers/${caregiverId}/`, payload);
    return response.data;
  },

  /**
   * Update a single caregiver-participant link's per-link fields
   * (currently relationship_type). Scoped to one study; does not touch the
   * caregiver's global profile.
   */
  updateLink: async (caregiverId, linkId, payload) => {
    const response = await api.patch(
      `/api/caregivers/${caregiverId}/links/${linkId}/`,
      payload,
    );
    return response.data;
  },

  // ---- caregiver-facing auth (FECOA-1320) ----

  /**
   * Caregiver login by caregiver_id + PIN, scoped by the Study Code of the
   * participants they care for (FECOA-2156). Setup links handed out before
   * that carry a sponsor instead, which the API still accepts.
   */
  login: async ({ caregiver_id, pin, study_code, sponsor_id }) => {
    const response = await api.post('/api/caregivers/login/', {
      caregiver_id,
      pin,
      ...(study_code ? { study_code } : { sponsor_id }),
    });
    const data = response.data;
    if (data?.access_token) {
      // FECOA-1514: caregiver session lives in sessionStorage (clears on tab close).
      setCaregiverAuthToken(data.access_token);
      sessionStorage.setItem(
        'caregiverSession',
        JSON.stringify({
          caregiver_id: data.caregiver_id,
          sponsor_id: data.sponsor_id ?? sponsor_id,
          study_code: data.study_code ?? study_code ?? null,
          requires_pin_change: !!data.requires_pin_change,
        }),
      );
    }
    return data;
  },

  /** Caregiver-initiated PIN change. */
  changePin: async ({ new_pin, current_pin }) => {
    const response = await api.post('/api/caregivers/me/change-pin/', {
      new_pin,
      ...(current_pin ? { current_pin } : {}),
    });
    // Update cached requires_pin_change flag.
    try {
      const session = JSON.parse(sessionStorage.getItem('caregiverSession') || '{}');
      session.requires_pin_change = false;
      sessionStorage.setItem('caregiverSession', JSON.stringify(session));
    } catch (_) {
      /* noop */
    }
    return response.data;
  },

  /** Redeem an invite token and set initial PIN. */
  acceptInvite: async ({ token, new_pin }) => {
    const response = await api.post('/api/caregivers/invite/accept/', {
      token,
      new_pin,
    });
    const data = response.data;
    if (data?.access_token) {
      // FECOA-1514: caregiver session lives in sessionStorage (clears on tab close).
      setCaregiverAuthToken(data.access_token);
      sessionStorage.setItem(
        'caregiverSession',
        JSON.stringify({
          caregiver_id: data.caregiver_id,
          requires_pin_change: false,
        }),
      );
    }
    return data;
  },

  /**
   * Re-mint the caregiver access token (FECOA-1514, AC2). Caregivers have no
   * refresh token, so the frontend swaps the current still-valid token for a
   * fresh one while the caregiver is active, keeping an in-progress survey
   * session alive past the short per-token ceiling. Called (throttled) by the
   * idle timer on activity. A 401 here means the token already lapsed; the api
   * interceptor handles the redirect to the expired-login page.
   */
  extendSession: async () => {
    const response = await api.post('/api/caregivers/me/extend/');
    const data = response.data;
    if (data?.access_token) {
      setCaregiverAuthToken(data.access_token);
    }
    return data;
  },

  /** Clear the caregiver session client-side. */
  logout: () => {
    setCaregiverAuthToken(null);
    sessionStorage.removeItem('caregiverSession');
  },

  /** Linked-participants list for the logged-in caregiver. Backend infers
   * scope from JWT (user_type='caregiver'). Currently driven by the per-link
   * query — same shape as the SC-facing list. */
  myLinks: async () => {
    const response = await api.get('/api/caregivers/me/links/');
    return response.data;
  },

  // ---- caregiver ObsRO survey-taking (FECOA-1320) ----

  /** Open ObsRO assignment queue for the logged-in caregiver. */
  getMyAssignments: async (date) => {
    const response = await api.get('/api/caregivers/me/assignments/', {
      params: date ? { date } : {},
    });
    return response.data;
  },

  /** Load all screens for a caregiver assignment (fast-nav survey taking). */
  loadScreens: async (assignmentId) => {
    const response = await api.post('/api/caregivers/me/load-screens/', {
      assignment_id: assignmentId,
    });
    return response.data;
  },

  /** Save one caregiver answer. */
  saveResponse: async (assignmentId, questionId, responseData) => {
    const response = await api.post('/api/caregivers/me/responses/', {
      assignment_id: assignmentId,
      question_id: questionId,
      response_data: responseData,
    });
    return response.data;
  },

  /** Finalize a caregiver survey submission. */
  submitSurvey: async (assignmentId) => {
    const response = await api.post('/api/caregivers/me/submit/', {
      assignment_id: assignmentId,
    });
    return response.data;
  },
};

export default caregiverService;
